import { FC, createContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Backdrop, Dialog, Fade } from '@mui/material'
import withStyles, { ClassNameMap } from '@mui/styles/withStyles'

import useFullwidthModalDetect from 'hooks/useFullwidthModalDetect'
import useQueryParams from 'hooks/useQueryParams'
import { finishErrorAlert } from 'redux/alert/alert-actions'
import { setCustomEvent } from 'redux/googleAnalytics/slice'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { themeStyles } from 'themes'

import { COMPONENT_EXTENDER } from 'extender'

export const ModalFormsContext = createContext(null)

const LimitedTokensModal = COMPONENT_EXTENDER('components/core/Forms/LimitedTokensForm/Tabs/Modal')
const CreditCardViewFormModal = COMPONENT_EXTENDER('components/core/Forms/CreditCardViewForm/CreditCardViewForm-modal')
const BankAccountFormModal = COMPONENT_EXTENDER('components/core/Forms/BankAccountForm/BankAccountForm-modal')
const DigitalAssetsModal = COMPONENT_EXTENDER('components/core/Forms/DigitalAssetsFrom/DigitalAssets/DigitalAssets-modal')
const AssetsFormModal = COMPONENT_EXTENDER('components/core/Forms/AssetsForm/AssetsForm-modal')
const OrderCardFormModal = COMPONENT_EXTENDER('components/core/Forms/OrderCardForm')
const OrderCurrencyAccountFormModal = COMPONENT_EXTENDER('components/core/Forms/OrderCurrencyAccountForm/OrderCurrencyAccountForm-modal')
const OrderBankAccountModal = COMPONENT_EXTENDER('components/core/Forms/OrderBankAccountForm/OrderBankAccountForm-modal')
const FileUploadFormModal = COMPONENT_EXTENDER('components/core/Forms/FileUploadForm/FileUploadForm-modal')
const PathHandlerModal = COMPONENT_EXTENDER('components/common/PathHandlerModal')
const PayFormModal = COMPONENT_EXTENDER('components/core/Forms/PayForm/PayForm-modal')
const TransactionsViewModal = COMPONENT_EXTENDER('components/core/Transactions/TransactionsViewModal')
const PreviewFileModal = COMPONENT_EXTENDER('components/core/Forms/PreviewFile/PreviewFile-modal')
const CreateNewMessageModal = COMPONENT_EXTENDER('components/core/Settings/Support/CreateNewMessage/CreateNewMessage-modal')
const EmailConfirmationModal = COMPONENT_EXTENDER('components/core/Forms/EmailConfirmationForm/EmailConfirmationForm-modal')
const Activate2FAModal = COMPONENT_EXTENDER('components/core/Activate2FA/components/Activate2FA-modal')
const CompanyPaymentsBankDepositModal = COMPONENT_EXTENDER('components/core/CompanyPayments/BankDeposit/BankDepositPayment-modal')
const CompanyPaymentsCardModal = COMPONENT_EXTENDER('components/core/CompanyPayments/Card/CardPayment-modal')
const CompanyPaymentsOnlineBankModal = COMPONENT_EXTENDER('components/core/CompanyPayments/OnlineBank/OnlineBankPayment-modal')
const VerifyPrimaryAddressFormModal = COMPONENT_EXTENDER('components/core/Forms/VerifyPrimaryAddressForm')
const QuickDepositModal = COMPONENT_EXTENDER('components/core/Forms/QuickDeposit/QuickDeposit-modal')
const SuccessModalCard = COMPONENT_EXTENDER('components/core/Cards/SuccessModalCard/SuccessModalCard-modal')
const SuspendedModalCard = COMPONENT_EXTENDER('components/core/Cards/SuspendedModalCard')
const EmailResetModal = COMPONENT_EXTENDER('components/modals/EmailResetModal')
const ConfirmationModal = COMPONENT_EXTENDER('components/modals/ConfirmationModal')
const VirtualAddressFormModal = COMPONENT_EXTENDER('components/core/Forms/VirtualAddressForm/VirtualAddressForm-modal')
const ShippingAddressFormModal = COMPONENT_EXTENDER('components/modals/ShippingAddressFormModal')
const BusinessApplyFormModal = COMPONENT_EXTENDER('components/modals/BusinessApply')

const enableLedgerRedirect = ['AssetsFormModal', 'BankAccountFormModal', 'QuickDepositModal']
const fullScreenModals = ['PreviewFileModal']
const deactivationStatuses = ['Suspended', 'Deactivated']

interface IProps {
    children: React.ReactNode
    classes: ClassNameMap
}

const generateModals = modalProps => ({
    AssetsFormModal: <AssetsFormModal {...modalProps} />,
    BankAccountFormModal: <BankAccountFormModal {...modalProps} />,
    CreditCardViewFormModal: <CreditCardViewFormModal {...modalProps} />,
    DigitalAssetsModal: <DigitalAssetsModal {...modalProps} />,
    LimitedTokensModal: <LimitedTokensModal {...modalProps} />,
    OrderCardFormModal: <OrderCardFormModal {...modalProps} />,
    OrderCurrencyAccountFormModal: <OrderCurrencyAccountFormModal {...modalProps} />,
    OrderBankAccountModal: <OrderBankAccountModal {...modalProps} />,
    FileUploadFormModal: <FileUploadFormModal {...modalProps} />,
    PathHandlerModal: <PathHandlerModal {...modalProps} />,
    PayFormModal: <PayFormModal {...modalProps} />,
    TransactionsViewModal: <TransactionsViewModal {...modalProps} />,
    PreviewFileModal: <PreviewFileModal {...modalProps} />,
    CreateNewMessageModal: <CreateNewMessageModal {...modalProps} />,
    EmailConfirmationModal: <EmailConfirmationModal {...modalProps} />,
    Activate2FAModal: <Activate2FAModal {...modalProps} />,
    CompanyPaymentsBankDepositModal: <CompanyPaymentsBankDepositModal {...modalProps} />,
    CompanyPaymentsCardModal: <CompanyPaymentsCardModal {...modalProps} />,
    CompanyPaymentsOnlineBankModal: <CompanyPaymentsOnlineBankModal {...modalProps} />,
    VerifyPrimaryAddressFormModal: <VerifyPrimaryAddressFormModal {...modalProps} />,
    QuickDepositModal: <QuickDepositModal {...modalProps} />,
    SuccessModalCard: <SuccessModalCard {...modalProps} />,
    EmailResetModal: <EmailResetModal {...modalProps} />,
    ConfirmationModal: <ConfirmationModal {...modalProps} />,
    VirtualAddressFormModal: <VirtualAddressFormModal {...modalProps} />,
    ShippingAddressFormModal: <ShippingAddressFormModal {...modalProps} />,
    BusinessApplyFormModal: <BusinessApplyFormModal {...modalProps} />,
})

type TModalKeys = keyof ReturnType<typeof generateModals>

type TModalProps = {
    modal?: TModalKeys
} & Record<string, any>

interface IModalValues {
    open: TModalKeys | null
    props: TModalProps
}

const ModalForms: FC<IProps> = ({ children, classes }) => {
    const isFullwidthModal = useFullwidthModalDetect()
    const { setSearchParams, deleteSearchParam } = useQueryParams()
    const [modalOpen, setModalOpen] = useState<IModalValues>({ open: null, props: {} })
    const dispatch = useAppDispatch()
    const errorAlert = useAppSelector(state => state.alert.errorAlert)
    const ledgerSuccess = useAppSelector(state => state.ledger.success)
    const currentUser = useAppSelector(state => state.user.currentUser)

    const { open, props } = modalOpen

    const ledgerRedirectStarting = enableLedgerRedirect.includes(open) && ledgerSuccess?.LEDGER_TOP_UP_WITH_NO_CARD_DATA
    const disableOnClose = !!ledgerRedirectStarting
    const isFullScreen = fullScreenModals.includes(open)
    const isModalDisabled = deactivationStatuses.includes(currentUser?.status) && open !== 'CreateNewMessageModal'

    const openModalGAEvent = (label: string, source: string) => {
        dispatch(
            setCustomEvent({
                eventAction: 'openModal',
                eventCategory: 'modal',
                value: label,
                kwargs: { source },
            })
        )
    }

    const replaceUrlForFullWidthModal = (path: string) => {
        if (!isFullwidthModal) {
            return
        }

        setSearchParams('modal', path)
    }

    const replaceUrlForFullWidthModalBack = () => {
        if (!isFullwidthModal) {
            return
        }

        deleteSearchParam('modal')
    }

    const closeModalGAEvent = (label: string) => {
        dispatch(
            setCustomEvent({
                eventAction: 'closeModal',
                eventCategory: 'modal',
                value: label,
            })
        )
    }

    const handleModalOpen = (props: TModalProps) => {
        openModalGAEvent(props.modal, props?.source)
        replaceUrlForFullWidthModal(props?.modal)
        errorAlert && dispatch(finishErrorAlert())
        setModalOpen({ ...modalOpen, open: props?.modal, props })
    }

    const handleClose = () => {
        closeModalGAEvent(modalOpen.open)
        errorAlert && dispatch(finishErrorAlert())
        setModalOpen({ ...modalOpen, open: null })
    }

    const modalValues = {
        handleModalOpen,
        handleClose,
        open,
        props,
    }

    const modalProps = {
        ...props,
        close: () => {
            closeModalGAEvent(modalOpen.open)
            setModalOpen({ ...modalOpen, open: null })
            replaceUrlForFullWidthModalBack()
        },
    }

    const Modals = generateModals(modalProps)

    useEffect(() => {
        const handleBeforeUnload = () => {
            closeModalGAEvent(modalOpen.open)

            setModalOpen({ ...modalOpen, open: null })
        }

        window.addEventListener('popstate', handleBeforeUnload)

        return () => {
            window.removeEventListener('popstate', handleBeforeUnload)
        }
    }, [modalOpen.open])

    return (
        <ModalFormsContext.Provider value={{ modalValues }}>
            <Dialog
                className={classes.modal}
                open={!!open}
                onClose={disableOnClose ? null : handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                classes={{
                    paper: classes.paperDialogWidth,
                    container: isFullScreen ? classes.paperContainerFullWidth : classes.MuiDialogContainer,
                }}
                fullScreen={isFullScreen}
                fullWidth={isFullScreen}
            >
                <Fade in={!!open}>
                    <div className={classes.paperDialog}>{isModalDisabled ? <SuspendedModalCard {...modalProps} /> : Modals[open]}</div>
                </Fade>
            </Dialog>
            {children}
        </ModalFormsContext.Provider>
    )
}

export default withStyles(themeStyles.useModalStyles)(ModalForms)
